import { Container, useMediaQuery, useTheme, Snackbar, Alert, Box } from '@mui/material';
import React, { useState, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Invoice, UIMappedInvoice, TableInvoice } from '../../../../types/ndisSection/invoiceTypes';
import InvoiceForm from './InvoiceForm';
import InvoiceList from './InvoiceList';
import InvoiceView from './InvoiceView';
import PrintComponent from './PrintComponent';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'app/store';
import { fetchSingleInvoice } from 'slices/NdisSlices/finance/InvoiceSlice';

export const mapToUIMappedInvoice = (invoice: Invoice): UIMappedInvoice => ({
  id: invoice.id || invoice.invoice_number || '',
  invoiceNumber: invoice.invoice_number || '',
  customerName: invoice.customer_name || '',
  address: invoice.address || '',
  cityStateZip: invoice.city_state_zip || '',
  date: invoice.issued_date || '',
  issuedPeriodDate_start: invoice.issued_period_date_start || '',
  issuedPeriodDate_end: invoice.issued_period_date_end || '',
  dueDate: invoice.due_date || '',
  totalAmount: invoice.Amount || 0,
  status: invoice.participant_approved ? 'completed' : 'unpaid',
  paymentDetails: {
    BSB: invoice.bsb || '',
    Account: invoice.account || '',
    ABN: invoice.abn || '',
    NDIS_Registration_Number: invoice.ndis_number || '',
    Contact: invoice.contact || ''
  },
  invoiceItems:
    invoice.shifts?.map((shift) => ({
      description: shift.support_item_name || '',
      quantity: shift.quantity || 0,
      price: shift.price || 0,
      status: invoice.participant_approved ? 'completed' : 'unpaid'
    })) || [],
  client_reference: invoice.client_reference || '',
  registrationnumber: invoice.registration_number || '',
  ndisnumber: invoice.ndis_number || '',
  authorisedby: invoice.authorised_by || '',
  participantapproved: invoice.participant_approved || false,
  inkindfundingprogram: invoice.in_kind_funding_program || '',
  claimtype: invoice.claim_type || '',
  gstcode: invoice.gst_code || '',
  supportnumber: invoice.shifts?.[0]?.support_number || '',
  Support_Item_Name: invoice.shifts?.[0]?.support_item_name || '',
  shifts: invoice.shifts || [],
  summary: {
    total_hours: invoice.Hours || invoice.summary?.total_hours || 0,
    total_quantity: invoice.summary?.total_quantity || 0,
    total_amount_due: invoice.Amount || invoice.summary?.total_amount_due || 0
  },
  avatar: invoice.avatar || '',
  workLoad_Report: invoice.workLoad_Report || {}
});

export const mapToTableInvoice = (invoice: Invoice): TableInvoice => ({
  id: invoice.id || invoice.invoice_number || '',
  invoiceNumber: invoice.invoice_number || '',
  customerName: invoice.customer_name || '',
  date: invoice.issued_date || '',
  totalHours: invoice.Hours || invoice.summary?.total_hours || 0,
  totalAmount: invoice.Amount || invoice.summary?.total_amount_due || 0,
  avatar: invoice.avatar || ''
});

interface PrintWrapperProps {
  invoice: Invoice | null;
  printRef: React.MutableRefObject<HTMLDivElement | null>;
}

const PrintWrapper: React.FC<PrintWrapperProps> = ({ invoice, printRef }) => {
  if (!invoice) return null;
  return (
    <Box ref={printRef} key={`box-${invoice.invoice_number}`}>
      <PrintComponent
        invoiceData={mapToUIMappedInvoice(invoice)}
        key={`print-${invoice.invoice_number}`}
      />
    </Box>
  );
};

const FinanceTab: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch<AppDispatch>();
  const { current_invoice } = useSelector((state: RootState) => state.invoice || {});
  const [view, setView] = useState<'list' | 'form' | 'view'>('list');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const selectedInvoiceRef = useRef<Invoice | null>(null);
  const [fortnightDate, setFortnightDate] = useState<string>('');
  const [previousView, setPreviousView] = useState<'list' | 'view'>('list');
  const [printError, setPrintError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isPrintPending, setIsPrintPending] = useState<boolean>(false);
  const selectedProjectId = useSelector(
    (state: RootState) => (state as any).homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected.selectedServiceTitle || ''
  );
  const parsedUserDetails = JSON.parse(localStorage.getItem('user-details') || '{}');
  const printRef = useRef<HTMLDivElement | null>(null);

  const handlePrintAction = useReactToPrint({
    content: () => {
      console.log('printRef.current:', printRef.current);
      if (!printRef.current) {
        console.error('printRef is null, cannot print');
        setPrintError('Print component not ready');
        setSnackbarOpen(true);
        setIsPrinting(false);
        setIsPrintPending(false);
      }
      return printRef.current;
    },
    pageStyle: `
      @page { 
        size: A4;
        margin: 15mm;
      }
      @media print { 
        body { 
          -webkit-print-color-adjust: exact;
          margin: 0;
          padding: 0;
          background: white;
        }
        html, body {
          width: 210mm;
          height: 297mm;
        }
      }
    `,
    onAfterPrint: () => {
      console.log('Print completed, resetting isPrinting and isPrintPending');
      setSelectedInvoice(null);
      setIsPrinting(false);
      setIsPrintPending(false);
    }
  });

  const handlePrint = useCallback(
    async (invoice: Invoice) => {
      if (!invoice.invoice_number) {
        console.error('Invoice number missing:', invoice);
        setPrintError('Invoice number is missing');
        setSnackbarOpen(true);
        return;
      }

      if (isPrintPending) {
        console.log('Print is pending, ignoring request for:', invoice.invoice_number);
        return;
      }

      setIsPrintPending(true);
      console.log('handlePrint called for invoice:', invoice.invoice_number, 'view:', view);

      // Reset printRef
      printRef.current = null;

      // Update ref and state
      selectedInvoiceRef.current = invoice;
      setSelectedInvoice(invoice);
      setIsPrinting(true);

      // Optimize: reuse current_invoice if available
      if (
        current_invoice &&
        !Array.isArray(current_invoice) &&
        current_invoice.invoice_number === invoice.invoice_number
      ) {
        console.log('Reusing current_invoice:', current_invoice);
        selectedInvoiceRef.current = current_invoice;
        setSelectedInvoice(current_invoice);
        setTimeout(() => {
          console.log('Triggering print for invoice (reused):', current_invoice.invoice_number);
          handlePrintAction();
        }, 50);
        return;
      }

      const filterDate = fortnightDate
        ? new Date(fortnightDate.split(' - ')[0].split(': ')[1].split('/').reverse().join('-'))
            .toISOString()
            .split('T')[0]
        : new Date().toISOString().split('T')[0];
      try {
        const payload = {
          user_whoRequested_id: parsedUserDetails.name || '',
          group_id: selectedProjectId || '',
          service_name_id: (selectedServiceTitle || '').replace(' ', '_'),
          nested_component_id: 'Invoice',
          invoice_id: invoice.invoice_number,
          client_name: invoice.customer_name || '',
          filter_date: filterDate
        };

        console.log('Fetching invoice with payload:', payload);
        const result = await dispatch(fetchSingleInvoice(payload)).unwrap();
        console.log('Fetched invoice result:', result);

        const fetchedInvoice = Array.isArray(current_invoice)
          ? current_invoice.find((inv) => inv.invoice_number === invoice.invoice_number) || null
          : current_invoice;

        if (!fetchedInvoice) {
          console.error('No fetched invoice found for:', invoice.invoice_number);
          setPrintError('Invoice data not found');
          setSnackbarOpen(true);
          setIsPrintPending(false);
          return;
        }

        console.log('Setting selectedInvoice:', fetchedInvoice);
        selectedInvoiceRef.current = fetchedInvoice;
        setSelectedInvoice(fetchedInvoice);
        setTimeout(() => {
          console.log('Triggering print for invoice:', fetchedInvoice.invoice_number);
          handlePrintAction();
        }, 50);
      } catch (err) {
        console.error('Print fetch failed:', err);
        setPrintError('Failed to fetch invoice data for printing');
        setSnackbarOpen(true);
        setIsPrintPending(false);
      }
    },
    [
      dispatch,
      current_invoice,
      parsedUserDetails.name,
      selectedProjectId,
      selectedServiceTitle,
      fortnightDate,
      view,
      isPrintPending
    ]
  );

  const handleView = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    selectedInvoiceRef.current = invoice;
    setPreviousView('list');
    setView('view');
  };

  const handleEdit = (invoice: Invoice) => {
    console.log('Editing invoice:', invoice);
    setSelectedInvoice(invoice);
    selectedInvoiceRef.current = invoice;
    setView('form');
  };

  const handleDelete = (invoice: Invoice) => {
    console.log(`Delete invoice with ID: ${invoice.id || invoice.invoice_number}`);
  };

  const handleCreate = () => {
    setSelectedInvoice(null);
    selectedInvoiceRef.current = null;
    setView('form');
  };

  const handleBack = () => {
    setView(previousView);
    if (previousView === 'list') {
      setSelectedInvoice(null);
      selectedInvoiceRef.current = null;
    }
  };

  const handleFormSubmit = (data: UIMappedInvoice) => {
    console.log('Form submitted:', data);
    handleBack();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setPrintError(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {view === 'list' && (
        <InvoiceList
          onView={handleView}
          onEdit={handleEdit}
          onPrint={handleView} // Note: This calls handleView instead of handlePrint
          onDelete={handleDelete}
          onCreate={handleCreate}
          isSmallScreen={isSmallScreen}
          setFortnightDate={setFortnightDate}
        />
      )}
      {view === 'form' && (
        <InvoiceForm
          invoice={selectedInvoice}
          onSubmit={handleFormSubmit}
          onCancel={handleBack}
          isSmallScreen={isSmallScreen}
          fortnightDate={fortnightDate}
        />
      )}
      {view === 'view' && selectedInvoice && (
        <InvoiceView
          invoice={selectedInvoice}
          onBack={handleBack}
          onEdit={() => handleEdit(selectedInvoice)}
          isSmallScreen={isSmallScreen}
          fortnightDate={fortnightDate}
        />
      )}
      <Box sx={{ display: 'none' }}>
        {isPrinting && <PrintWrapper invoice={selectedInvoiceRef.current} printRef={printRef} />}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {printError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FinanceTab;
